import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import * as css from './CaseStudy.m.css';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { Image } from '../../widgets/Image';
import { Section } from '../../widgets/Section';
import { Technologies } from '../../widgets/Technologies';
import { Text } from '../../widgets/Text';
import ContentWrapper from '../../widgets/ContentWrapper';
import ListColumns from '../../widgets/ListColumns';
import SpeechBubble from '../../widgets/SpeechBubble';
import Summary from '../../widgets/Summary';

const intuitCardLeft = require('../../assets/images/work-mockups/intuit-card-top-right.png');
const intuitCardLeftHighRes = require('../../assets/images/work-mockups/intuit-card-top-right@2x.png');
const intuitCardRight = require('../../assets/images/work-mockups/intuit-card-bottom-right.png');
const intuitCardRightHighRes = require('../../assets/images/work-mockups/intuit-card-bottom-right@2x.png');
const intuitMockup = require('../../assets/images/work-mockups/intuit-mockup.png');
const intuitMockupHighRes = require('../../assets/images/work-mockups/intuit-mockup@2x.png');

const factory = create();

export const CaseStudyIntuit = factory(function CaseStudyIntuit() {
	return (
		<div classes={css.root}>
			<Head
				title="Web Application Authorization Migration | SitePen"
				description="Learn how SitePen helped migrate the complete authorization layer of a major accounting provider’s premier online product suite."
			/>

			<Hero background="level1BlueGreen" align="center" blurbWidth="md" paddingBottom="sm">
				{{
					title: 'Case Study',
					xlTitle: 'Intuit',
					blurb: (
						<virtual>
							Replacing the entire authorization system of a major online product with
							zero user impact takes great care and dedication. Policies for thousands
							of endpoints need to be migrated, and granular feature toggles
							introduced to give users flexibility in customizing their workflows
							through new role combinations. A major accounting firm entrusted SitePen
							with the bulk of the migration efforts.
							<Technologies
								icons={[
									'javascript',
									'typescript',
									'dojo-toolkit',
									'react',
									'jest',
									'java',
									'junit',
									'mockito',
									'open-agent-policy',
									'splunk'
								]}
							/>
						</virtual>
					)
				}}
			</Hero>

			<Section type="leading" background>
				<ContentWrapper size="small">
					<Text type="section">The Situation</Text>
					<p>
						<Text type="body">
							A major accounting solution provider undertook a project to upgrade the
							authorization layer of their primary web application and engaged SitePen
							to help in the migration efforts. The project aimed to replace
							access-control list checks that were hard-coded into the product with a{' '}
							<a
								href="https://www.openpolicyagent.org/"
								target="_blank"
								rel="noopener"
							>
								policy-based resource model
							</a>{' '}
							using an externalized authorization service for greater flexibility.
						</Text>
					</p>
					<p>
						<Text type="body">
							A substantial application codebase comprising thousands of API endpoints
							required uplifting, which meant working with ownership teams across the
							entire organization. Millions of existing customers expecting 24/7
							uptime meant delicate project phasing was needed to succeed. An
							inventory of the existing authorization checks accrued over decades of
							product development had to be established and migrated to new resources
							and policies. The new policies would be integrated alongside existing
							ACL checks, then validated that they accurately met all existing
							authorization security requirements. The client could only migrate users
							to the new system after they were fully satisfied that the new
							authorization system would not disrupt any existing functionality.
						</Text>
					</p>
				</ContentWrapper>
			</Section>

			<Section shaded type="leading" background>
				<ContentWrapper size="small">
					<div classes={[css.topImageContainer]}>
						<img
							loading="lazy"
							classes={[css.topLeftImage]}
							src={intuitCardLeft}
							srcset={`${intuitCardLeftHighRes} 2x`}
							alt=""
						/>
						<img
							loading="lazy"
							classes={[css.topRightImage]}
							src={intuitCardRight}
							srcset={`${intuitCardRightHighRes} 2x`}
							alt=""
						/>
					</div>
					<div classes={css.mainImageContainer}>
						<Image
							src={intuitMockup}
							highResSrc={intuitMockupHighRes}
							align="center"
							lazy
						/>
					</div>
					<Text type="section">The Solution</Text>
					<Text type="body">
						SitePen was brought into the project to lead the resource policy migration
						effort within the system backend and to help enhance the frontend
						application to support fine-grained feature toggling based on the new
						resource model.
					</Text>
					<Text type="body">
						The backend policy migration process involved several major phases:
					</Text>
					<ListColumns
						columnCount={1}
						style="disc"
						paddingLeft="sm"
						itemPaddingBottom="sm"
					>
						<Text type="body">
							Analyzing the ACL checks that the product’s existing service endpoints
							were performing.
						</Text>
						<Text type="body">
							Defining new resources and policies to match the existing checks.
						</Text>
						<Text type="body">
							Integrating the new authorization primitives throughout the application,
							in parallel with the existing ACL system, to avoid disrupting users.
						</Text>

						<Text type="body">
							Validating that the new authorization decisions accurately met the
							existing security requirements.
						</Text>
						<Text type="body">
							Switching over to use the new resources and policies for authorization
							enforcement.
						</Text>
					</ListColumns>
					<Text type="body">
						This process was iterated across the application codebase to cover all
						existing authorization requirements. The team continuously deployed new
						authorization features as they were migrated, with SitePen liaising with all
						owners responsible for each area to approve, release, and deploy the new
						checks.
					</Text>
					<Text type="body">
						Frontend work included implementing new feature requirements that specified
						how configurable resource actions translated into new or modified user
						workflows, for example, to allow for read-only reviewer users or workflow
						approvers that only had access to a subset of an organization’s data. Given
						the broad scope of the project, this process included feature analysis to
						understand the existing functionality and how it could be extended to
						support new requirements across the entire suite of application capabilities
						and workflows. Once a consistent and achievable implementation had been
						agreed upon, SitePen helped the client to implement the new user interface
						functionality.
					</Text>
				</ContentWrapper>
			</Section>

			<Section>
				<ContentWrapper>
					<Summary blurbGutter="medium" imageGutter="none">
						{{
							title: 'The Result',
							blurb: (
								<virtual>
									<p>
										SitePen successfully migrated thousands of the most-used
										authorization checks within the product and helped implement
										many of the new front-end requirements, enabling the client
										to meet their project goals.
									</p>
									<p>
										Decoupling authorization decision-making from the main
										accounting product allows authorization policies to be
										developed and deployed independently, reducing turnaround
										time when adding or modifying resources. The client can now
										resolve critical user or product security issues separately
										from the heavyweight deployment pipeline of the whole
										application codebase, minimizing security risks through
										immediate resolution. Separating resources and their
										policies from the product implementation also allows the
										creation of fine-grained roles that give users greater
										flexibility in how they structure their workflows across the
										product.
									</p>
								</virtual>
							),
							image: (
								<SpeechBubble>
									{{
										main: `Best experience working with an external firm. So professional, diligent, thorough, and on time. It was fun collaborating with SitePen. They’re just the type of people I would hire full-time – without hesitation.`,
										subtitle1: 'Senior Engineering Manager',
										subtitle2: 'Intuit'
									}}
								</SpeechBubble>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section type="trailing" shaded>
				<ConnectStrip trailing />
			</Section>
		</div>
	);
});

export default CaseStudyIntuit;
